/**
 * EchoBrandMark - the Kata.fit "Echo" identity: three repeated K strokes
 * showing repetition, consistency, and incremental improvement. Earlier
 * strokes are muted gunmetal/steel; the final stroke is warm off-white.
 * There is no reddish square attached to the K - a muted reddish accent
 * belongs only to the period in the KATA.FIT wordmark, never to this mark.
 *
 * The mark is frameless by design. Native app-icon fields are authored by
 * regimen-mobile/scripts/generate-brand-assets.mjs because the OS supplies
 * the mask; this component never draws a redundant frame.
 *
 * Geometry is read from ../brand/echoGeometry.json, the single source of
 * truth shared with the asset-generation script, so this component and
 * every generated native/web icon stay in sync.
 *
 * Motion: the repetitions assemble once on mount, then hold still. There is
 * no perpetual breathing, spinning, or particle motion, and
 * prefers-reduced-motion always renders the final, fully-assembled mark.
 */
import React, { useEffect, useState } from 'react';
import './EchoBrandMark.css';
import geometry from '../brand/echoGeometry.json';

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
const readReducedMotion = () => (
  typeof window !== 'undefined' && Boolean(window.matchMedia?.(reducedMotionQuery).matches)
);

const VARIANTS = ['expressive', 'compact', 'micro'];

function EchoBrandMark({
  variant = 'expressive',
  size,
  animate = true,
  tickTrail = false,
  decorative = false,
  role = 'img',
  label = 'Kata.fit',
  className = '',
}) {
  const [reduceMotion, setReduceMotion] = useState(readReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(reducedMotionQuery);
    const handleChange = event => setReduceMotion(event.matches);
    setReduceMotion(media.matches);
    if (media.addEventListener) media.addEventListener('change', handleChange);
    else media.addListener?.(handleChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', handleChange);
      else media.removeListener?.(handleChange);
    };
  }, []);

  const resolvedVariant = VARIANTS.includes(variant) ? variant : 'expressive';
  const shouldAnimate = animate && !reduceMotion;
  const spec = geometry[resolvedVariant];
  const accessibilityProps = decorative
    ? { 'aria-hidden': true }
    : { role, 'aria-label': label, ...(role === 'status' ? { 'aria-live': 'polite' } : {}) };
  const style = size ? { '--echo-mark-size': typeof size === 'number' ? `${size}px` : size } : undefined;

  return (
    <svg
      className={[
        'echo-brand-mark',
        `echo-brand-mark-${resolvedVariant}`,
        shouldAnimate ? 'echo-brand-mark-animate' : '',
        className,
      ].filter(Boolean).join(' ')}
      viewBox={spec.viewBox}
      style={style}
      {...accessibilityProps}
    >
      {!decorative && <title>{label}</title>}
      {spec.strokes && (
        <g fill="none" strokeLinecap="square" strokeLinejoin="miter">
          {spec.strokes.map((stroke, index) => (
            <React.Fragment key={stroke.d}>
              {tickTrail && resolvedVariant === 'expressive' && index === spec.strokes.length - 1 && (
                <path
                  d={stroke.d}
                  className="echo-stroke echo-stroke-detach"
                  style={{
                    '--echo-stroke-color': geometry.palette.steel,
                    '--echo-stroke-width': geometry.expressive.strokes[1].width,
                  }}
                />
              )}
              <path
                d={stroke.d}
                className={`echo-stroke echo-stroke-${index + 1}`}
                style={{
                  '--echo-stroke-color': geometry.palette[stroke.color],
                  '--echo-stroke-width': stroke.width,
                }}
              />
            </React.Fragment>
          ))}
        </g>
      )}
      {spec.solid && (
        <path
          d={spec.solid.d}
          className="echo-stroke echo-stroke-final"
          style={{ '--echo-stroke-color': geometry.palette[spec.solid.color] }}
        />
      )}
    </svg>
  );
}

export default EchoBrandMark;
